import { useRef } from 'react';

/**
 * Get the value of something from a previous render.
 *
 * https://www.developerway.com/posts/implementing-advanced-use-previous-hook
 *
 * @param value - The value to get the previous value of.
 * @param options
 * @param options.ignoreUndefined - Skip setting undefined to the previous value.
 * @example
 *
 * function Component({ count }) {
 *   // Undefined on first render and after count has been undefined.
 *   const prevCount = usePrevious(count);
 *   // Will never be undefined after getting an initial previous value.
 *   const prevDefinedCount = usePrevious(count, { ignoreUndefined: true });
 *
 *   return <div>Count went from {prevCount} to {count}</div>;
 * }
 */
export function usePrevious<T>(
	value: T,
	{ ignoreUndefined = false }: { ignoreUndefined?: boolean } = {},
): T | undefined {
	const ref = useRef<{ prev: T | undefined; value: T }>({
		prev: undefined,
		value,
	});
	const savedValue = ref.current.value;

	if (value !== savedValue) {
		let newPrev: T | undefined = savedValue;
		if (ignoreUndefined && savedValue === undefined) {
			newPrev = ref.current.prev;
		}
		ref.current = {
			prev: newPrev,
			value,
		};
	}

	return ref.current.prev;
}
